import { Box, formattedCurrency, getCurrencySign, Pagination, usePagination, useUpdateQueryParams } from 'vopay';
import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import useFetchInvoices from '#shared/hooks/useFetchInvoices';
import useFetchStatement from '#shared/hooks/useFetchStatement';
import useFetchInvoicePdf from '#shared/hooks/useFetchInvoicePdf';
import { downloadCsv, downloadPdf } from '#shared/utils/fileDownload';
import { useAlertDialog } from '#shared/providers/AlertDialog';
import { useToast } from '#shared/providers/Toast';
import { useBlockingLoader } from '#shared/providers/Loader';
import { Button, Skeleton, Table, TableFiltersContainer } from '#shared/components/UI';
import InvoicesSearchForm from './InvoicesSearchForm';
import useAccountAndBilling from '../useAccountAndBilling';
import { SECOND_TAB_VARIANTS } from '../constants';
import { InvoicesSearchParams } from '../types';

const PER_PAGE = 20;

type QueryParams = InvoicesSearchParams;

// Internal to the feature root: reads userPermissions from the feature context rather than taking it
// as a prop.
export default function BillingInvoices() {
  const { userPermissions, billingApi, secondTab } = useAccountAndBilling();
  const { invoices, fetching, fetchInvoices } = useFetchInvoices(billingApi.fetchInvoices);
  const { fetchStatement } = useFetchStatement(billingApi.fetchStatement);
  const { fetchInvoicePdf } = useFetchInvoicePdf(billingApi.fetchInvoicePdf);

  const { hasInvoiceDownload, hasStatementDownload, statementLabel } = SECOND_TAB_VARIANTS[secondTab];

  const location = useLocation();
  const alertDialog = useAlertDialog();
  const toast = useToast();
  const blockingLoader = useBlockingLoader();
  const { t, i18n } = useTranslation('translations', { keyPrefix: 'Shared.BillingInvoices' });
  const { t: tButtons } = useTranslation('translations', { keyPrefix: 'Shared.Buttons' });
  const { t: tDialogue } = useTranslation('translations', { keyPrefix: 'Shared.InvoicesDialogue' });
  const { t: tErrors } = useTranslation('translations', { keyPrefix: 'Shared.Errors' });

  const queryParams: QueryParams = useMemo(() => {
    const params = new URLSearchParams(location.search);

    return { searchTerm: params.get('searchTerm') || '' };
  }, [location.search]);

  const filtersApplied = useMemo(() => Object.values(queryParams).some((val) => val !== ''), [queryParams]);

  const updateQueryParams = useUpdateQueryParams<QueryParams>();

  const { pageCount, pageIndex, offset, setPageIndex } = usePagination(invoices.length, { pageSize: PER_PAGE });

  // Page data, so it renders a skeleton from the hook's `fetching` rather than raising the
  // fullscreen loader — that's reserved for flows the user must not interact through, which
  // here is the two downloads below.
  async function search(searchParams: InvoicesSearchParams) {
    try {
      await fetchInvoices({ SearchText: searchParams.searchTerm });
    } catch (error) {
      // The hook already logged. A failed list load doesn't have to be acknowledged and doesn't stop
      // the flow, so it's a toast rather than a dialogue — and the backend's message is interpolated
      // into localized framing instead of being shown to the user bare.
      toast.error(
        t('fetchError', {
          defaultValue: "We couldn't load your invoices: {{errorMessage}}",
          errorMessage:
            error instanceof Error
              ? error.message
              : tErrors('unknown', { defaultValue: 'An unexpected error occurred. Please try again.' }),
        }),
      );
    }
  }

  // Mount-only on purpose: afterwards the search form drives both the URL and the refetch, so
  // depending on searchTerm here would fetch twice per search.
  useEffect(() => {
    search(queryParams);
  }, []);

  // Both downloads fail the same way, and neither has a backend message worth quoting — the hook logged
  // it and the user's only move is to retry, so the copy stays the generic one the dialogue carried.
  function showDownloadFailed() {
    return alertDialog.caution(
      tDialogue('title', { defaultValue: 'Download Failed' }),
      tDialogue('subTitle', {
        defaultValue: 'Your request could not be completed. Please try again in a few minutes.',
      }),
      { confirmLabel: tButtons('okay', { defaultValue: 'Okay' }) },
    );
  }

  // The alert is awaited outside the try/finally so the blocking loader is already down when it opens.
  async function downloadInvoice(invoiceId: string) {
    blockingLoader.show();

    let hasFailed = false;

    try {
      const res = await fetchInvoicePdf({ InvoiceID: invoiceId });
      downloadPdf(res.InvoicePDF, 'invoice');
    } catch {
      hasFailed = true;
    } finally {
      blockingLoader.hide();
    }

    if (hasFailed) await showDownloadFailed();
  }

  async function downloadStatement(invoiceId: string) {
    blockingLoader.show();

    let hasFailed = false;

    try {
      const res = await fetchStatement({ InvoiceID: invoiceId });

      // Clients get a CSV, partners a PDF; the format arrives with the bytes from the host op.
      if (res.format === 'csv') {
        downloadCsv(res.data, 'statement');
      } else {
        downloadPdf(res.data, 'statement');
      }
    } catch {
      hasFailed = true;
    } finally {
      blockingLoader.hide();
    }

    if (hasFailed) await showDownloadFailed();
  }

  // The backend sends a bare date string with no locale, so rendering it raw put an en-US-shaped
  // date under a French header. 'PP' is date-fns' locale-aware long date; an unparseable value
  // renders as it arrived rather than as "Invalid Date".
  function formatInvoiceDate(invoiceDate: string) {
    const parsed = new Date(invoiceDate);

    if (Number.isNaN(parsed.getTime())) return invoiceDate;

    return format(parsed, 'PP', { locale: i18n.language.startsWith('fr') ? fr : undefined });
  }

  const rows = useMemo(
    () =>
      [...invoices]
        .sort((a, b) => new Date(b.InvoiceDate).getTime() - new Date(a.InvoiceDate).getTime())
        .map((invoice) => ({ id: String(invoice.InvoiceID), data: invoice }))
        .slice(offset, offset + PER_PAGE),
    [invoices, offset],
  );

  return (
    <Box jc="sb" flx="col">
      <Box flx="col" gap="sm">
        <TableFiltersContainer filtersApplied={filtersApplied}>
          <TableFiltersContainer.Trigger />

          <TableFiltersContainer.Content>
            <InvoicesSearchForm
              searchParams={queryParams}
              onSearch={(searchParams) => {
                setPageIndex(0);
                updateQueryParams(searchParams);
                search(searchParams);
              }}
            />
          </TableFiltersContainer.Content>
        </TableFiltersContainer>
      </Box>

      {/* Column, not Box's default row: in a row-flex parent the skeleton's wrapper span shrinks to its
          content, which is nothing, so the skeleton rendered at zero width and the tab looked blank while
          a search was in flight. */}
      <Box flx="col">
        {fetching ? (
          <Skeleton height="320px" />
        ) : (
          <Table
            id="invoice-list"
            zebra
            columns={[
              {
                id: 'DateIssued',
                header: t('Table.dateIssued', { defaultValue: 'Date Issued' }),
                align: 'left',
                template: ({ data }) => formatInvoiceDate(data.InvoiceDate),
              },
              {
                id: 'InvoiceNumber',
                header: t('Table.invoiceNumber', { defaultValue: 'Invoice Number' }),
                align: 'left',
                field: 'InvoiceNumber',
              },
              {
                id: 'Amount',
                header: t('Table.amount', { defaultValue: 'Amount' }),
                align: 'left',
                template: ({ data }) => getCurrencySign(data.CurrencyShortForm) + formattedCurrency(data.Total),
              },
              {
                id: 'Currency',
                header: t('Table.currency', { defaultValue: 'Currency' }),
                align: 'left',
                field: 'CurrencyShortForm',
              },
              {
                id: 'view',
                header: '',
                align: 'right',
                template: ({ data }) => (
                  <Box gap="sm" jc="fe">
                    {hasInvoiceDownload && (
                      <Button
                        id={`download-invoice-pdf-${data.InvoiceID}`}
                        size="small"
                        disabled={!userPermissions.canManageBilling}
                        onClick={() => {
                          downloadInvoice(data.InvoiceID);
                        }}
                      >
                        {t('Table.downloadInvoicePDF', { defaultValue: 'Download Invoice PDF' })}
                      </Button>
                    )}

                    {hasStatementDownload && (
                      <Button
                        id={`download-statement-${data.InvoiceID}`}
                        size="small"
                        disabled={!userPermissions.canManageBilling}
                        onClick={() => {
                          downloadStatement(data.InvoiceID);
                        }}
                      >
                        {t(statementLabel.key, { defaultValue: statementLabel.defaultValue })}
                      </Button>
                    )}
                  </Box>
                ),
              },
            ]}
            rows={rows}
          />
        )}
      </Box>

      <Box width="100%" jc="center" mt="lg" mb="sm">
        {pageCount > 1 && (
          <Pagination
            pageCount={pageCount}
            forcePageNumber={pageIndex}
            handlePageClick={(e) => {
              setPageIndex(e.selected);
            }}
          />
        )}
      </Box>
    </Box>
  );
}
